import { useEffect } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import SiteNav, { BookingButton, WriteMeLink } from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageSeo from "@/components/site/PageSeo";
import LogoMarquee from "@/components/site/LogoMarquee";
import { useLanguage } from "@/contexts/LanguageContext";
import { landingPages } from "@/data/landingPages";

// Extra FR article links shown in "À lire aussi" for specific pages.
const extraArticles: Record<string, { to: string; label: string }[]> = {
  "directeur-marketing-externalise": [
    { to: "/blog/cmo-externalise-vs-agence-vs-recrutement", label: "CMO externalisé, agence ou CDI : le comparatif" },
    { to: "/blog/strategie-marketing-pme-bordeaux", label: "Stratégie marketing pour PME à Bordeaux" },
  ],
};

// Portrait for /a-propos — set to the image URL once provided. No placeholder is shown meanwhile.
const ABOUT_PHOTO_URL: string | null = null;

type Props = { pageKey: string; forceLanguage?: "fr" | "en"; pathOverride?: string };

const LandingPage = ({ pageKey, forceLanguage, pathOverride }: Props) => {
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    if (forceLanguage && language !== forceLanguage) setLanguage(forceLanguage);
  }, [forceLanguage, language, setLanguage]);

  const lang = forceLanguage ?? language;
  const page = landingPages[pageKey];
  if (!page) throw new Error(`Unknown landing page: ${pageKey}`);
  const c = page[lang] ?? page.fr;
  const fr = lang === "fr";
  const path = pathOverride ?? page.path;

  const book = fr ? "Réserver un appel" : "Book a call";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {pageKey !== "fractional-cmo-france" && <PageSeo path={path} title={c.title} description={c.description} />}
      <SiteNav />

      <main>
        <section className="border-b border-border px-5 pb-16 pt-20 md:pb-20 md:pt-28">
          <div className="mx-auto max-w-4xl">
            <nav className="mb-8 text-sm text-muted-foreground" aria-label={fr ? "Fil d'Ariane" : "Breadcrumb"}>
              <Link to="/" className="transition-colors hover:text-primary">
                {fr ? "Accueil" : "Home"}
              </Link>
              <span className="px-2">/</span>
              {(path.includes("/services/") || pageKey === "directeur-marketing-externalise" || pageKey === "fractional-cmo-france") && (
                <>
                  <Link to="/services" className="transition-colors hover:text-primary">Services</Link>
                  <span className="px-2">/</span>
                </>
              )}
              <span className="text-foreground">{pageKey === "a-propos" ? (fr ? "À propos" : "About") : c.h1}</span>
            </nav>
            {pageKey === "a-propos" && ABOUT_PHOTO_URL && (
              <img src={ABOUT_PHOTO_URL} alt="Tiphany Rigaudias, Fractional CMO basée à Bordeaux" className="mb-10 h-40 w-40 rounded-full object-cover" width={160} height={160} />
            )}
            <h1 className="text-4xl font-bold leading-[1.1] md:text-6xl">
              {c.h1}
              <span className="text-primary">.</span>
            </h1>
            <p className="mt-8 text-lg leading-relaxed text-muted-foreground md:text-xl">{c.intro}</p>
            {c.brief && (
              <div className="mt-8 rounded-lg bg-secondary p-7">
                <p className="text-sm font-bold uppercase tracking-wide">{fr ? "En bref" : "In short"}</p>
                <p className="mt-3 leading-relaxed text-muted-foreground">{c.brief}</p>
              </div>
            )}
            <div className="mt-10">
              <BookingButton label={book} location={`${pageKey}_hero`} large />
            </div>
          </div>
        </section>

        <LogoMarquee title={fr ? "Marques accompagnées" : "Brands I've worked with throughout my career"} />

        {c.proof && (
          <section className="border-b border-border px-5 py-16">
            <div className="mx-auto grid max-w-5xl grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-4">
              {c.proof.map((item) => (
                <div key={item.label}>
                  <strong className="block text-4xl font-bold text-primary md:text-5xl">{item.value}</strong>
                  <span className="mt-3 block text-sm leading-relaxed text-muted-foreground">{item.label}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="px-5 py-20 md:py-24">
          <div className="mx-auto max-w-4xl space-y-16">
            {c.sections.map((section, index) => (
              <article key={section.h2}>
                <h2 className="text-3xl font-bold leading-tight md:text-4xl">
                  {section.h2}
                  {/[.?!…]$/.test(section.h2.trim()) ? null : <span className="text-primary">.</span>}
                </h2>
                <div className="mt-6 space-y-5 text-lg leading-relaxed text-muted-foreground">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
                {section.bullets && (
                  <ul className="mt-6 space-y-3">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-3 text-lg leading-relaxed text-muted-foreground">
                        <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
                {section.table && (
                  <div className="mt-8 overflow-x-auto">
                    <table className="w-full min-w-[560px] border-collapse text-left">
                      <thead>
                        <tr className="border-b-2 border-foreground">
                          {section.table.head.map((h) => <th key={h} scope="col" className="py-3 pr-4 text-sm font-bold uppercase">{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.rows.map((row) => (
                          <tr key={row[0]} className="border-b border-border align-top">
                            {row.map((cell, i) => i === 0
                              ? <th key={i} scope="row" className="py-4 pr-4 font-semibold">{cell}</th>
                              : <td key={i} className="py-4 pr-4 leading-relaxed text-muted-foreground">{cell}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {section.after?.map((p) => <p key={p} className="mt-6 text-lg leading-relaxed text-muted-foreground">{p}</p>)}
                {section.link && (
                  <Link to={section.link.to} className="mt-6 inline-block font-semibold text-primary hover:underline">{section.link.label}</Link>
                )}
                {section.link2 && (
                  <Link to={section.link2.to} className="mt-6 inline-block font-semibold text-primary hover:underline md:ml-6">{section.link2.label}</Link>
                )}
                {index === 0 && (
                  <div className="mt-10 rounded-lg bg-secondary p-7">
                    <p className="text-lg font-semibold">
                      {fr ? "30 minutes pour identifier vos leviers prioritaires." : "30 minutes to identify your priority levers."}
                    </p>
                    <div className="mt-5">
                      <BookingButton label={book} location={`${pageKey}_mid`} />
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>

        {c.faq.length > 0 && <section className="border-t border-border bg-secondary px-5 py-20">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold md:text-4xl">
              {fr ? "Questions fréquentes" : "Frequently asked questions"}
              <span className="text-primary">.</span>
            </h2>
            <dl className="mt-10 space-y-8">
              {c.faq.map((item) => (
                pageKey === "a-propos" ? (
                  <details key={item.q} className="group border-t border-border pt-6">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-bold">
                      <dt>{item.q}</dt>
                      <span className="text-primary transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                    </summary>
                    <dd className="mt-3 leading-relaxed text-muted-foreground">{item.a}</dd>
                  </details>
                ) : (
                <div key={item.q} className="border-t border-border pt-6">
                  <dt className="text-lg font-bold">{item.q}</dt>
                  <dd className="mt-3 leading-relaxed text-muted-foreground">
                    {item.a}
                    {item.link && <> <Link to={item.link.to} className="font-semibold text-primary hover:underline">{item.link.label}</Link></>}
                  </dd>
                </div>
                )
              ))}
            </dl>
          </div>
        </section>}

        <section className="border-t border-border px-5 py-16">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-xl font-bold">{fr ? "À lire aussi" : "Read next"}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {page.related.map((key) => {
                const related = landingPages[key];
                if (!related) return null;
                return (
                  <Link
                    key={key}
                    to={related.path}
                    className="rounded-lg border border-border p-5 transition-colors hover:border-primary"
                  >
                    <span className="font-semibold">{related.shortName ?? ((((related[lang] ?? related.fr).h1.split(/[—:]/)[0]) ?? related.fr.h1).trim())}</span>
                    <ArrowRight className="mt-4 h-4 w-4 text-primary" aria-hidden="true" />
                  </Link>
                );
              })}
              {fr && (extraArticles[pageKey] ?? []).map((a) => (
                <Link key={a.to} to={a.to} className="rounded-lg border border-border p-5 transition-colors hover:border-primary">
                  <span className="font-semibold">{a.label}</span>
                  <ArrowRight className="mt-4 h-4 w-4 text-primary" aria-hidden="true" />
                </Link>
              ))}
            </div>
            <p className="mt-8 text-sm text-muted-foreground">
              {fr ? "Basée à Bordeaux, disponible partout en France." : "Based in Bordeaux, available across France."}
            </p>
          </div>
        </section>

        <section id="booking" className="scroll-mt-16 bg-foreground px-5 py-24 text-center text-background md:py-28">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold md:text-5xl">
              {fr ? "Parlons de votre situation" : "Let's talk about your situation"}
              <span className="text-primary">.</span>
            </h2>
            <p className="mt-6 text-lg text-background/60">
              {fr ? "30 minutes pour identifier vos leviers prioritaires." : "30 minutes to identify your priority growth levers."}
            </p>
            <div className="mt-10">
              <BookingButton label={book} location={`${pageKey}_final`} large /><WriteMeLink />
            </div>
            <p className="mt-6 text-sm text-background/50">
              {fr
                ? "Fractional CMO · 2 à 12 jours par mois · Diagnostic en 2 semaines"
                : "Fractional CMO · 2 to 12 days per month · Diagnosis in 2 weeks"}
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default LandingPage;
